import React, { useEffect, useState, useRef } from "react";
import { Box, Flex, HStack, keyframes, Heading, Text } from "@chakra-ui/react";
import { LOMA_DARK_TEAL } from "../constants/colors";
import ContentBox from "./shared/ContentBox";

interface LoadingExperienceProps {
  currentStep: string;
  steps?: string[];
  forceStepIndex?: number;
  currentStepKey: string;
}

const shimmer = keyframes`
  0% { background-position: 100% 0%; }
  100% { background-position: -100% 0%; }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

const defaultSteps = [
  "Analyzing",
  "Preparing",
  "Searching",
  "Matching",
  "Finalizing",
];

const LoadingExperience: React.FC<LoadingExperienceProps> = ({
  currentStep,
  steps = defaultSteps,
  forceStepIndex,
  currentStepKey,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [timingText, setTimingText] = useState<string>("(This step may take up to 30 seconds)");

  const getStepTiming = (importKey: string | null): string => {
    if (!importKey) {
      return "(This step may take up to 30 seconds)";
    }

    switch (importKey) {
      case "social_data_json":
        return "(This step may take up to 25 seconds)";
      case "queries":
        return "(This step may take up to 10 seconds)";
      case "listings":
        return "(This step may take up to 30 seconds)";
      case "sorted_listings":
        return "(This step may take up to 10 seconds)";
      case "listings_data":
        return "(This step may take up to 20 seconds)";
      case "custom_idea":
      case "custom_try_again":
        return "(This step may take up to 25 seconds)";
      default:
        return "(This step may take up to 30 seconds)";
    }
  };

  // Keep track of previously seen steps to auto-advance
  const prevStepRef = useRef<string>("");

  // Auto increment steps when currentStep changes
  useEffect(() => {
    // Skip first render
    if (prevStepRef.current === "") {
      prevStepRef.current = currentStep;
      setTimingText(getStepTiming(currentStepKey));
      return;
    }

    // If currentStep changed, increment the step counter
    if (currentStep !== prevStepRef.current) {
      // Increment step but don't exceed max steps
      setActiveIndex((prev) => Math.min(prev + 1, steps.length - 1));
      prevStepRef.current = currentStep;
    }

    setTimingText(getStepTiming(currentStepKey));
  }, [currentStep, steps.length]);

  // Handle forceStepIndex if provided
  useEffect(() => {
    if (typeof forceStepIndex === "number") {
      setActiveIndex(Math.min(forceStepIndex, steps.length - 1));
    }
  }, [forceStepIndex, steps.length]);

  const shimmerStyle = {
    position: "relative",
    color: "transparent",
    background:
      "linear-gradient(to right, rgba(0,0,0,0.8), rgba(100,100,100,0.4), rgba(200,200,200,0.1), rgba(100,100,100,0.4), rgba(0,0,0,0.8))",
    backgroundSize: "200% 100%",
    backgroundPosition: "100% 0%",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    animation: `${shimmer} 2s linear infinite`,
  };

  return (
    <ContentBox>
      {/* Step tracker */}
      <HStack spacing={0} mb={{ base: 8, md: 12 }} px={{ base: 2, md: 0 }}>
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            {index > 0 && (
              <Box
                flex={1}
                h="2px"
                bg={index <= activeIndex ? LOMA_DARK_TEAL : "gray.200"}
                transition="background 0.3s ease"
              />
            )}
            <Box position="relative" zIndex={1}>
              <Box
                w={{ base: "20px", md: "24px" }}
                h={{ base: "20px", md: "24px" }}
                borderRadius="full"
                bg={index <= activeIndex ? LOMA_DARK_TEAL : "gray.200"}
                borderWidth={2}
                borderColor={index <= activeIndex ? LOMA_DARK_TEAL : "gray.200"}
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="white"
                fontWeight="bold"
                fontSize={{ base: "2xs", md: "xs" }}
                transition="all 0.3s ease"
                animation={
                  index === activeIndex
                    ? `${pulse} 2s infinite ease-in-out`
                    : "none"
                }
                boxShadow={
                  index === activeIndex ? `0 0 10px ${LOMA_DARK_TEAL}` : "none"
                }
              >
                {index < activeIndex ? "✓" : index + 1}
              </Box>
            </Box>
          </React.Fragment>
        ))}
      </HStack>

      {/* Central loading area */}
      <Flex
        direction="column"
        align="center"
        justify="center"
        h={{ base: "240px", md: "260px" }}
      >
        <Heading
          fontSize={{ base: "xl", md: "1.4rem" }}
          textAlign="center"
          sx={shimmerStyle}
        >
          {currentStep}
          <br />
          <Text
            as="span"
            fontSize={{ base: "sm", md: "md" }}
            fontWeight="normal"
            color="white"
          >
            {timingText}
          </Text>
        </Heading>
      </Flex>
    </ContentBox>
  );
};

export default LoadingExperience;
